// Needed to provide a non-scrolling layout for positioning the keyline
import styled from "@emotion/styled";
import { sizes } from "../../../constants/spacing";
import { ScrollAreaGutter } from "../types";
import { resolveGutterLength } from "../utils/resolveGutterLength";

export interface ScrollAreaScrollStyledProps {
    /**
     * Which way to orient. Vertical is the default.
     */
    direction: "vertical" | "horizontal";

    /**
     * gutter is the amount of padding that should be applied to the StyledScrollAreaScroll component.
     */
    gutter: ScrollAreaGutter;
    /**
     * Whether or not the container is scrollable, helps with padding adjustments to account for the presence of the scrollbar
     */
    isScrollable: boolean;
}

// Parent of body content. Provides scrolling context
export const ScrollAreaScrollStyled = styled("div", {
    shouldForwardProp: (propName) => {
        switch (propName) {
            case "direction":
            case "gutter":
            case "isScrollable":
                return false;
            default:
                return true;
        }
    },
})<ScrollAreaScrollStyledProps>`
    display: flex;
    flex-direction: ${({ direction }) => (direction === "horizontal" ? "row" : "column")};
    flex: 1 1 auto;

    ${({ direction }) => {
        if (direction === "horizontal") {
            return `
                max-height: 100%;
            `;
        } else {
            return `
                max-width: 100%;
            `;
        }
    }}

    padding: ${({ direction, gutter }) => {
        // Don't use gutter for horizontal scroll areas. We would rather see people place their own
        // padding on the content.
        if (direction === "horizontal") {
            return "";
        }
        switch (gutter) {
            case "none":
                return "0px";
            case "legacy":
                return `0px ${sizes.large}px 0px ${sizes.xLarge}px`;
            default:
                return `0px ${resolveGutterLength(gutter)};`;
        }
    }};
    // Keep scrollbar 8px off the edge for a legacy gutter to preserve back-compat.
    // Otherwise we want consumers to be able to specify their own margins.
    margin-right: ${({ direction, gutter }) =>
        direction === "vertical" && gutter === "legacy" ? sizes.xSmall : 0}px; // keep scrollbar 8px off the edge

    overflow-y: ${({ direction }) => (direction === "horizontal" ? "hidden" : "auto")};
    overflow-x: ${({ direction }) => (direction === "horizontal" ? "auto" : "hidden")};

    @media (min-width: 320px) and (max-width: 480px) {
        height: 100%;
    }
    // This odd linethat doesn't seem to do anything is needed for a bugfix
    // where an ancestor picked up the height of the hidden scroll content
    // https://git.lab.smartsheet.com/design-systems-team/lodestar-core/-/issues/649
    transform: scale(1);
`;
